/**
 * Weight display helpers.
 *
 * `pets.profile.weights[]` carries a free-text `weight` (real prod values
 * include `"11 lbs lb"`, `"8 kg"`, `"12"`) plus a structured `unit` (and, since
 * 2026-08-30, a numeric `weight_kg`).
 *
 * History: the `unit` column used to be a hardcoded constant `'kg'` — measured
 * 2026-08-29 it was `'kg'` on 130/130 prod entries while 74 of those strings
 * said lb/lbs, and consumers that appended `unit` to `weight` rendered
 * `"10.5 lbs lb kg"`. The upstream defect was FIXED in ada-core on 2026-08-30
 * (weight-history trigger now derives unit + weight_kg from the text; the 71
 * mislabelled US-prod entries were backfilled — vic/ada-core#8).
 *
 * This module KEEPS its in-band policy anyway, deliberately: **trust the
 * in-band unit, never the `unit` column, and never convert.** Reasons it stays
 * even though `.unit` is now correct in US prod: China cgm is NOT backfilled;
 * it is defense-in-depth against any future writer regressing the column; and a
 * parsed free-text weight is not a sound basis for arithmetic on a clinical
 * number, so nothing below multiplies anything — these helpers only decide what
 * to *assert*, and assert nothing where the unit is unknown rather than guess.
 * (Prefer the numeric `weight_kg` if you ever need to compute — it is canonical
 * kg; but display stays text-only.)
 */

export type WeightUnit = 'kg' | 'lb'

/**
 * The unit a weight string carries in-band, or null when it carries none.
 *
 * Matches `lb`/`lbs` and `kg` as whole words so a breed- or note-like string
 * cannot trip it. `"11 lbs lb"` reads as `lb` — the upstream double-suffix is
 * still one unit, repeated.
 */
export const parseWeightUnit = (weight: unknown): WeightUnit | null => {
  if (typeof weight !== 'string') return null
  if (/\blbs?\b/i.test(weight)) return 'lb'
  if (/\bkgs?\b/i.test(weight)) return 'kg'
  return null
}

/**
 * A weight cell, with a unit appended ONLY when the value does not already
 * carry one.
 *
 * `fallbackUnit` is used solely for a bare numeric value (19 prod entries).
 * The `unit` column is not consulted, because it is wrong more often than it
 * is right.
 */
export const formatWeightWithUnit = (weight: unknown, fallbackUnit = 'kg'): string => {
  const text = typeof weight === 'string' ? weight.trim() : String(weight ?? '').trim()
  if (!text) return ''
  return parseWeightUnit(text) ? text : `${text} ${fallbackUnit}`
}

/**
 * The axis label for a weight SERIES: the shared in-band unit when every
 * entry agrees, otherwise `''`.
 *
 * An empty label is the honest outcome for a mixed or unknown series — the
 * points are plotted unconverted, so asserting any single unit over them
 * would be a claim the data does not support. (Measured 2026-08-29: no prod
 * pet currently mixes lb and kg, so in practice this resolves to a real unit
 * or to unknown, not to a mixed series. It must still handle the mixed case,
 * because nothing upstream prevents it.)
 */
export const resolveSeriesUnit = (weights: ReadonlyArray<{ weight?: unknown }>): string => {
  const units: WeightUnit[] = []
  for (const entry of weights) {
    const unit = parseWeightUnit(entry?.weight)
    if (unit && !units.includes(unit)) units.push(unit)
  }
  return units.length === 1 ? units[0] : ''
}
